import React, { useEffect, useState } from "react";
import { Stack } from "@mui/joy";
import {
  loadTossPayments,
  TossPaymentsPayment,
} from "@tosspayments/tosspayments-sdk";
import { useRecoilValue } from "recoil";
import { userAtom } from "../../recoil/userAtom";
import { useQuery } from "@tanstack/react-query";
import {
  PAYMENT_DOMAIN,
  PAYMENT_PLAN_UPGRADE_ORDER_DETAIL_ENDPOINT,
  paymentApi,
  PaymentPlanUpgradeOrderType,
} from "../../api/paymentApi";
import useSafeAreaInsets from "../../hooks/useSafeAreaInsets";
import ContentTemplateV2 from "../../components/task/content/ContentTemplateV2";
import { useSearchParams } from "react-router-dom";
import {
  getMultiPlanPrices,
  getPromotionPrices,
} from "../../components/task/payment/planTypes";

export default function TossPaymentsPlanUpgrade() {
  const tossPaymentsClientKey = process.env.REACT_APP_TOSSPAYMENTS_CLIENT_KEY!!;
  const [searchParams] = useSearchParams();
  const previousPlan = searchParams.get("previous_plan");
  const newPlan = searchParams.get("new_plan");
  const isCoachChangeRequested =
    searchParams.get("is_coach_change_requested") === "true";

  const [tossPaymentsPayment, setTossPaymentsPayment] =
    useState<TossPaymentsPayment | null>();

  const [order, setOrder] = useState<PaymentPlanUpgradeOrderType | null>(null);
  const [price, setPrice] = useState<number | null>(null);

  const user = useRecoilValue(userAtom);

  const { refetch: getPaymentOrder } = useQuery(
    [PAYMENT_DOMAIN, PAYMENT_PLAN_UPGRADE_ORDER_DETAIL_ENDPOINT],
    () => paymentApi.getPlanUpgradeOrderDetail(),
    {
      enabled: false,
      onSuccess: (data) => {
        if (data && data.result) {
          setOrder(data.result);

          // Compute price difference between plans with defensive checks.
          // 제휴 프로모션 전용가(우선) > pricingModel "ajk" 등 다중 플랜 오버라이드 > 기본가.
          const priceMap: Record<string, number> = getPromotionPrices(
            user?.promotion
          ) ??
            getMultiPlanPrices(user?.pricingModel) ?? {
              starter: 295000,
              challenger: 395000,
              master: 465000,
            };

          if (!previousPlan || !newPlan) {
            console.error(
              "Missing plan parameters: previousPlan or newPlan is undefined"
            );
            return; // defensive: stop if params are missing
          }

          const prev = priceMap[previousPlan];
          const next = priceMap[newPlan];

          if (prev === undefined || next === undefined) {
            console.error("Invalid plan parameters", {
              previousPlan,
              newPlan: newPlan,
            });
            return; // defensive: stop if plan names are invalid
          }

          const diff = next - prev;
          if (diff <= 0) {
            console.error("newPlan must be higher than previousPlan", {
              previousPlan,
              newPlan: newPlan,
            });
            return; // defensive: stop if upgrade is not to a higher plan
          }

          setPrice(diff);
        }
      },
    }
  );

  useEffect(() => {
    if (user?.accessToken) {
      getPaymentOrder();
    } else {
      console.error("유저 정보 없음");
    }
  }, []);

  async function requestTossPayments() {
    if (tossPaymentsPayment && order && price) {
      try {
        await tossPaymentsPayment?.requestPayment({
          method: "CARD",
          amount: {
            currency: "KRW",
            value: price,
          },
          orderId: order?.orderId!!,
          orderName: `${order?.orderName!!} ${newPlan === "challenger" ? "챌린저" : "마스터"} 플랜`,
          successUrl:
            window.location.origin +
            `/toss-payments/plan-upgrade/success?previous_plan=${previousPlan}&new_plan=${newPlan}&is_coach_change_requested=${isCoachChangeRequested}`,
          failUrl: window.location.origin + "/toss-payments/plan-upgrade/fail",
          customerEmail: order?.customerEmail,
          customerName: order.customerName,
          card: {
            useEscrow: false,
            flowMode: "DEFAULT",
            useCardPoint: false,
            useAppCardOnly: false,
          },
        });
      } catch (error) {
        // 에러 처리하기
        console.error(error);
      }
    }
  }

  useEffect(() => {
    async function fetchTossPaymentsPayment() {
      if (tossPaymentsClientKey && order?.customerKey) {
        const tossPayments = await loadTossPayments(tossPaymentsClientKey);
        const payment = tossPayments.payment({
          customerKey: order.customerKey,
        });

        setTossPaymentsPayment(payment);
      }
    }

    fetchTossPaymentsPayment();
  }, [tossPaymentsClientKey, order]);

  useEffect(() => {
    if (tossPaymentsPayment) {
      requestTossPayments();
    }
  }, [tossPaymentsPayment]);

  const { bottom: safeBottom } = useSafeAreaInsets();
  const [containerHeight, setContainerHeight] = useState<number>(
    window.innerHeight - safeBottom
  );

  const [floatingAreaHeight] = useState<number>(0);

  return (
    <ContentTemplateV2
      fullBleed
      disableBottomSafeArea
      isWide
      setContainerHeight={setContainerHeight}
    >
      {containerHeight > 0 && (
        <Stack sx={{ pb: "150px", opacity: floatingAreaHeight > 0 ? 1 : 0 }}>
          <Stack
            spacing={"30px"}
            justifyContent="center"
            alignItems={"center"}
            sx={{
              width: "100%",
              maxWidth: "660px",
              mx: "auto",
              px: "20px",
              // height: `calc(${containerHeight}px - ${floatingAreaHeight}px)`,
              // pt: "50px",
            }}
          ></Stack>
        </Stack>
      )}
    </ContentTemplateV2>
  );
}
